import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ds';
import {
  AUTH_BUTTON_STYLE,
  AuthError,
  AuthHeading,
  AuthLayout,
  TextField,
} from '@/components/auth/AuthLayout';
import { requestPasswordReset } from '@/lib/session';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !email.trim()) return;

    setBusy(true);
    setError('');
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the reset link.');
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      {sent ? (
        <div className="flex flex-col gap-5">
          <AuthHeading title="Check your inbox">
            If an account exists for {email.trim()}, we&rsquo;ve sent a link to reset its password.
          </AuthHeading>
          <Link
            to="/login"
            className="text-sm font-semibold text-forest underline-offset-2 hover:underline"
          >
            Back to sign in
          </Link>
        </div>
      ) : (
        <form className="flex flex-col gap-5" onSubmit={submit} noValidate>
          <AuthHeading title="Reset your password">
            Enter your email and we&rsquo;ll send you a secure reset link.
          </AuthHeading>

          <AuthError>{error}</AuthError>

          <TextField
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            autoFocus
            required
            placeholder="you@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={busy}
          />

          <Button type="submit" style={AUTH_BUTTON_STYLE} disabled={busy || !email.trim()}>
            {busy ? 'Sending link…' : 'Send reset link'}
          </Button>

          <p className="text-center text-sm text-ink-soft">
            Remembered it?{' '}
            <Link
              to="/login"
              className="font-semibold text-forest underline-offset-2 hover:underline"
            >
              Sign in
            </Link>
          </p>
        </form>
      )}
    </AuthLayout>
  );
}

export default ForgotPasswordPage;
